import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button, EmptyState, IconButton, InlineError, Modal, Skeleton } from '../../components/ui'
import { useTenant } from '../../tenant/useTenant'
import {
  useGetMyBookingQuery,
  useGetRescheduleAvailabilityQuery,
  useGetRescheduleDatesQuery,
  useRescheduleMyBookingMutation,
} from '../../services/accountApi'
import type { BookingSummary, Slot } from '../../types/storefront'
import { cn } from '../../utils/cn'
import {
  addDaysKey,
  BAND_ICON,
  BAND_LABEL,
  bandOf,
  dayOfMonth,
  formatLongDate,
  formatSlotTime,
  monthKey,
  monthShort,
  slotDate,
  todayInZone,
  weekdayShort,
  type SlotBand,
} from '../../utils/datetime'
import { bookingWhen, cancelPolicyNote } from './bookingDisplay'

const STRIP_DAYS = 14
const BANDS: SlotBand[] = ['morning', 'afternoon', 'evening']

/**
 * I3 / I8 — move a booking to another time. Services and specialists stay as
 * booked, so the grid is the booking's own (its current slot counts as free);
 * the server re-checks the chosen time before it moves anything.
 */
export function RescheduleBookingPage() {
  const { slug, bootstrap } = useTenant()
  const { id } = useParams<{ id: string }>()
  const bookingId = Number(id)
  const validId = Number.isFinite(bookingId) && bookingId > 0

  const booking = useGetMyBookingQuery({ slug, id: bookingId }, { skip: !validId })
  const [moved, setMoved] = useState<BookingSummary | null>(null)

  if (moved) return <Moved slug={slug} booking={moved} />

  return (
    <div className="container-page py-section-gap-md md:py-section-gap-lg">
      <Link
        to={`/${slug}/account/bookings/${bookingId}`}
        className="mb-12 inline-flex items-center gap-2 font-label-caps text-label-caps uppercase text-on-surface-variant transition-colors hover:text-primary"
      >
        <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
          arrow_back
        </span>
        Back to appointment
      </Link>

      <header className="mb-12 max-w-2xl">
        <h1 className="mb-4 font-display text-headline-lg-mobile text-primary md:text-headline-lg">
          Choose a new time
        </h1>
        <p className="font-body-lg text-body-lg text-on-surface-variant">
          Same services, same specialist — just a different slot. All times are shown in the salon&rsquo;s
          timezone ({bootstrap.timezone}).
        </p>
      </header>

      {booking.isLoading && <Skeleton lines={3} />}

      {booking.isError && <InlineError error={booking.error} onRetry={() => void booking.refetch()} />}

      {booking.data && !booking.data.can_reschedule && (
        <NotMovable slug={slug} booking={booking.data} cutoffHours={bootstrap.flags.cancel_cutoff_hours} />
      )}

      {booking.data?.can_reschedule && <SlotPicker booking={booking.data} onMoved={setMoved} />}
    </div>
  )
}

/* ------------------------------------------------------------ slot picker */

function SlotPicker({ booking, onMoved }: { booking: BookingSummary; onMoved: (b: BookingSummary) => void }) {
  const { slug, bootstrap } = useTenant()

  const today = useMemo(() => todayInZone(bootstrap.timezone), [bootstrap.timezone])
  const current = slotDate(booking.starts_at)
  const [stripStart, setStripStart] = useState(() => (current && current > today ? current : today))
  const [date, setDate] = useState(() => (current && current >= today ? current : today))
  const [chosen, setChosen] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const [reschedule, { isLoading: saving, error: saveError }] = useRescheduleMyBookingMutation()

  const strip = useMemo(
    () => Array.from({ length: STRIP_DAYS }, (_, i) => addDaysKey(stripStart, i)),
    [stripStart],
  )
  const months = useMemo(() => [...new Set(strip.map(monthKey))], [strip])

  const firstMonth = useGetRescheduleDatesQuery(
    { slug, id: booking.id, month: months[0] ?? '' },
    { skip: !months[0] },
  )
  const secondMonth = useGetRescheduleDatesQuery(
    { slug, id: booking.id, month: months[1] ?? '' },
    { skip: !months[1] },
  )

  const openDates = useMemo(() => {
    const all = [...(firstMonth.data ?? []), ...(secondMonth.data ?? [])]
    // No answer yet is not "closed": every day stays tappable until the months load.
    return all.length ? new Set(all) : null
  }, [firstMonth.data, secondMonth.data])

  const availability = useGetRescheduleAvailabilityQuery({ slug, id: booking.id, date })

  const slots = useMemo(() => availability.data ?? [], [availability.data])
  const bookable = useMemo(() => slots.filter((s) => s.available), [slots])

  const grouped = useMemo(() => {
    const buckets = new Map<SlotBand, Slot[]>()
    for (const slot of slots) {
      const band = bandOf(slot.start)
      const bucket = buckets.get(band)
      if (bucket) bucket.push(slot)
      else buckets.set(band, [slot])
    }
    return buckets
  }, [slots])

  // The booking's own time is in the grid too (its slot is freed for the search),
  // but moving to where it already is would change nothing.
  const isCurrent = (slot: Slot) => slot.start === booking.starts_at
  const chosenIsLive = !!chosen && bookable.some((s) => s.start === chosen && !isCurrent(s))

  const pickDate = (key: string) => {
    setDate(key)
    setChosen(null)
  }

  const confirm = async () => {
    if (!chosen) return
    const result = await reschedule({ slug, id: booking.id, startsAt: chosen })
    setConfirming(false)
    if ('data' in result && result.data) onMoved(result.data)
    // A refused time (just taken, notice period) leaves the grid stale; reload it.
    else void availability.refetch()
  }

  return (
    <>
      <div className="mb-12 rounded-lg border border-primary/10 bg-surface p-6">
        <p className="font-label-caps text-label-caps uppercase text-primary">Currently booked</p>
        <p className="mt-3 font-body-md text-body-md text-on-surface">{bookingWhen(booking)}</p>
        <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
          {booking.services.map((s) => s.name).join(', ')} · {booking.branch.name}
        </p>
      </div>

      <section aria-label="Choose a date" className="mb-12">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-headline-sm text-on-surface">{monthShort(stripStart)}</h2>
          <div className="flex gap-2">
            <IconButton
              icon="chevron_left"
              label="Previous week"
              disabled={stripStart <= today}
              onClick={() => {
                const next = addDaysKey(stripStart, -7)
                setStripStart(next < today ? today : next)
              }}
            />
            <IconButton icon="chevron_right" label="Next week" onClick={() => setStripStart(addDaysKey(stripStart, 7))} />
          </div>
        </div>

        <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
          {strip.map((key) => {
            const closed = openDates ? !openDates.has(key) : false
            const active = key === date
            return (
              <button
                key={key}
                type="button"
                onClick={() => pickDate(key)}
                disabled={closed}
                aria-pressed={active}
                aria-label={formatLongDate(key)}
                title={closed ? `${formatLongDate(key)} — no times` : undefined}
                className={cn(
                  'flex h-24 w-[72px] shrink-0 flex-col items-center justify-center gap-2 rounded-xl border transition-colors duration-300',
                  active
                    ? 'border-primary bg-primary text-on-primary'
                    : 'border-outline-variant bg-surface-container-lowest text-on-surface hover:border-primary hover:text-primary',
                  closed &&
                    'cursor-not-allowed border-outline-variant/50 bg-surface-container-lowest text-outline-variant opacity-50 hover:border-outline-variant/50 hover:text-outline-variant',
                )}
              >
                <span className="font-label-caps text-label-caps uppercase">{weekdayShort(key)}</span>
                <span className="font-display text-headline-sm">{dayOfMonth(key)}</span>
              </button>
            )
          })}
        </div>
      </section>

      <section aria-label="Choose a time" className="mb-12">
        <h2 className="mb-8 font-display text-headline-sm text-on-surface">{formatLongDate(date)}</h2>

        {availability.isLoading || availability.isFetching ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} shape="rect" className="h-12 w-full" />
            ))}
          </div>
        ) : availability.error ? (
          <InlineError error={availability.error} onRetry={availability.refetch} />
        ) : slots.length === 0 ? (
          <EmptyState
            icon="event_busy"
            title="No times on this day"
            body="The salon is either closed or fully booked. Pick another day from the strip above."
          />
        ) : bookable.length === 0 ? (
          <EmptyState
            icon="event_busy"
            title="Your specialist is fully booked"
            body="There is no free time on this day for your services with your specialist. Try another day."
          />
        ) : (
          <div className="flex flex-col gap-10">
            {BANDS.map((band) => {
              const items = grouped.get(band)
              if (!items?.length) return null
              return (
                <div key={band}>
                  <h3 className="mb-4 flex items-center gap-2 font-label-caps text-label-caps uppercase text-outline">
                    <span className="material-symbols-outlined text-[16px]">{BAND_ICON[band]}</span>
                    {BAND_LABEL[band]}
                  </h3>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                    {items.map((slot) => {
                      const mine = isCurrent(slot)
                      const open = slot.available && !mine
                      const active = slot.start === chosen
                      return (
                        <button
                          key={slot.start}
                          type="button"
                          disabled={!open}
                          onClick={() => setChosen(slot.start)}
                          aria-pressed={active}
                          className={cn(
                            'rounded-lg px-4 py-3 text-center font-body-md text-body-md transition-colors duration-300',
                            mine
                              ? 'cursor-default border border-dashed border-primary text-primary'
                              : open
                                ? active
                                  ? 'border-2 border-primary bg-primary/5 font-bold text-primary'
                                  : 'border border-outline-variant text-on-surface hover:border-primary hover:text-primary'
                                : 'cursor-not-allowed border border-outline-variant bg-surface-container-lowest text-outline-variant opacity-50 line-through',
                          )}
                          title={mine ? 'Your current time' : open ? undefined : 'Not available'}
                        >
                          {formatSlotTime(slot.start)}
                          {mine && <span className="block text-xs">Current</span>}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {saveError && (
        <div className="mb-6">
          <InlineError error={saveError} />
        </div>
      )}

      <div className="flex flex-col items-center gap-6 border-t border-surface-variant pt-8 md:flex-row">
        <Button size="lg" disabled={!chosenIsLive} onClick={() => setConfirming(true)}>
          Move my appointment
        </Button>
        <Link
          to={`/${slug}/account/bookings/${booking.id}`}
          className="font-body-md text-body-md text-primary underline underline-offset-4 transition-colors hover:text-primary-container"
        >
          Keep current time
        </Link>
      </div>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        icon="event_repeat"
        title="Move this appointment?"
        description={
          chosen
            ? `From ${bookingWhen(booking)} to ${formatLongDate(slotDate(chosen))} · ${formatSlotTime(chosen)}.`
            : undefined
        }
        footer={
          <>
            <Button variant="text" size="lg" onClick={() => setConfirming(false)}>
              Not yet
            </Button>
            <Button size="lg" loading={saving} onClick={() => void confirm()}>
              Yes, move it
            </Button>
          </>
        }
      />
    </>
  )
}

/* ------------------------------------------------------------ refusals */

function NotMovable({
  slug,
  booking,
  cutoffHours,
}: {
  slug: string
  booking: BookingSummary
  cutoffHours: number | null
}) {
  const scheduled = booking.status === 'pending' || booking.status === 'confirmed'
  const policy = cancelPolicyNote(cutoffHours)

  return (
    <div className="max-w-2xl">
      <p className="font-body-md text-body-md text-on-surface">
        {scheduled
          ? `This appointment can no longer be moved online${
              policy ? ` — the salon needs ${cutoffHours} hour${cutoffHours === 1 ? '' : 's'} of notice` : ''
            }. Please call the salon and they can help.`
          : 'This appointment can no longer be moved online. Please call the salon if you need to change it.'}
      </p>
      <Button variant="outlined" size="lg" className="mt-8" to={`/${slug}/account/bookings/${booking.id}`}>
        Back to appointment
      </Button>
    </div>
  )
}

/* ------------------------------------------------------------- moved */

function Moved({ slug, booking }: { slug: string; booking: BookingSummary }) {
  return (
    <div className="container-page py-section-gap-md md:py-section-gap-lg">
      <div className="mx-auto w-full max-w-2xl space-y-10 rounded-xl border border-primary/10 bg-surface-container-lowest p-8 text-center md:p-12">
        <div className="flex justify-center">
          <span
            aria-hidden="true"
            className="flex h-20 w-20 items-center justify-center rounded-full bg-surface-container-high text-primary"
          >
            <span className="material-symbols-outlined text-[36px]">event_available</span>
          </span>
        </div>

        <div className="space-y-4">
          <h1 className="font-display text-headline-lg-mobile text-primary md:text-headline-lg">Appointment moved</h1>
          <p className="font-body-lg text-body-lg text-on-surface-variant">
            You&apos;re now booked for {bookingWhen(booking)} at {booking.branch.name}.
          </p>
        </div>

        <div className="flex flex-col justify-center gap-4 md:flex-row">
          <Button to={`/${slug}/account/bookings/${booking.id}`} size="lg">
            View appointment
          </Button>
          <Button to={`/${slug}/account`} variant="outlined" size="lg">
            My appointments
          </Button>
        </div>
      </div>
    </div>
  )
}
